<template>
  <div>
    <div v-if="paymentSuccess" class="text-center">
      <v-alert text type="success">{{ paymentSuccessMsg }}</v-alert>
    </div>

    <template v-else-if="connectionError">
      <v-alert text type="warning"
        >There was a problem reaching Square. If this continues,
        <a :href="`mailto:${getContactEmail}`">email us</a> or try another
        method.<br />
        <small v-if="connectionErrorDetail">{{ connectionErrorDetail }}</small>
      </v-alert>
      <div class="text-center">
        <v-btn outlined @click="tryFindOrCreate">Try Again</v-btn>
      </div>
    </template>

    <template v-else>
      <div v-if="applePaySuccess">
        <div
          id="apple-pay-button"
          alt="apple-pay"
          @click="handlePaymentMethodSubmission(applePay)"
        >
          Loading Apple Pay
        </div>
        <div class="payment-border"><span>OR</span> </div>
      </div>
      <form v-show="squareFormLoaded" id="payment-form">
        <h2 class="text-h6 mb-3"
          >Click the link below to pay through square.</h2
        >

        <div id="card-container"></div>
        <v-btn
          id="card-button"
          type="button"
          depressed
          block
          class="mb-3 white--text"
          color="deep-orange darken-2"
          large
          @click="handlePaymentMethodSubmission(card)"
          :disabled="!squareFormLoaded || isSubmittingPayment"
          >Pay With Card</v-btn
        >
        <span id="payment-flow-message"></span>
        <v-alert text type="warning" v-if="paymentError"
          >There was a problem processing your order. If you continue to see
          this error please contact us at
          <a :href="`mailto:${getContactEmail}`">{{ getContactEmail }}</a
          ><br />
          <small>{{ paymentError.errorDetail }}</small></v-alert
        >
      </form>
      <transition name="fade" mode="out-in">
        <v-skeleton-loader
          v-if="!squareFormLoaded"
          type="table-heading,table-heading"
        />
      </transition>
    </template>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import SquareService from '@/services/SquareService'
import tokenize from '@/utils/square-tokenize'
import OrderModel from '@/models/OrderModel'
import {
  DELIVERY_FEE_ITEM_NAME,
  FAMILY_DISCOUNT_ITEM_NAME,
} from '@/constants/AppItemNames'
import { SQUARE_IS_IN_SANDBOX } from '@/constants/credentials'
import _get from 'lodash/get'
import { addHours, addMinutes, formatRFC3339 } from 'date-fns'

export default {
  name: 'BtnSquarePayment',
  components: {},
  props: {
    order: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      checkoutUrl: null,
      connectionError: '',
      connectionErrorDetail: null,
      squareFormLoaded: false,
      squareOrder: null,
      isSubmittingPayment: false,
      paymentSuccess: false,
      paymentError: false,
      payments: null,
      applePay: null,
      applePaySuccess: false,
      card: null,
      cardPayError: false,
    }
  },
  async mounted() {
    await this.initialize()
    await this.tryFindOrCreate()
  },
  computed: {
    ...mapGetters('shop', [
      'getSquareLocationID',
      'getContactEmail',
      'getSquareAppId',
    ]),
    ...mapGetters('auth', [
      'getAuthUserId',
      'getAuthUserEmail',
      'getAuthUserName',
    ]),
    ...mapGetters('customer', ['getCustomerDashboardLink']),
    lineItems() {
      return _get(this.order, 'products', [])
    }, // lineItems
    ticketNumber() {
      return _get(
        OrderModel.find(this.$route.params.orderId),
        'ticket_number',
        'Not Found'
      )
    }, // ticketNumber

    orderTotalInCents() {
      return this.order.grand_total
    }, // orderTotalInCents

    squareOrderData() {
      let lineItems = this.lineItems.map((li) => {
        return {
          name: li.item_name,
          quantity: `${li.quantity}`,
          itemType: 'ITEM',
          basePriceMoney: {
            amount: li.item_price,
            currency: 'USD',
          },
        }
      })
      let squareOrderData = {
        lineItems,
        locationId: this.getSquareLocationID,
        referenceId: `${this.$route.params.orderId}`,
        customerId: `${this.getAuthUserId}`,
        source: {
          name: 'Minimart',
        },
        note: `Minimart Ticket #${this.ticketNumber}`,
      }
      if (this.familyDiscountItem) {
        squareOrderData.discounts = [
          {
            name: 'Family Discount',
            type: 'FIXED_AMOUNT',
            scope: 'ORDER',
            amountMoney: {
              amount: Math.abs(
                Math.ceil(
                  // actually get it
                  _get(this.familyDiscountItem, 'item_price', 0)
                )
              ),
              currency: 'USD',
            },
          },
        ]
      }
      if (this.order.tax) {
        squareOrderData.taxes = [
          {
            name: 'Sales Tax',
            percentage: `${this.salesTaxPcnt}`,
            scope: 'ORDER',
            type: 'ADDITIVE',
          },
        ]
      }
      // actually get it

      if (this.deliveryFeeItem) {
        squareOrderData.serviceCharges = [
          {
            name: 'Delivery Fee',
            amountMoney: {
              amount: Math.ceil(_get(this.deliveryFeeItem, 'item_price', 0)),
              currency: 'USD',
            },
            calculationPhase: 'SUBTOTAL_PHASE',
            taxable: false,
          },
        ]
      }

      let note = _get(this.order, 'special_instructions') || ''
      if (note.length > 500) {
        let warning =
          '... Note Exceeded Length Limit for Square. Check Original on Minimart.'
        note = note.slice(0, 500 - warning.length)
        note += warning
      }
      squareOrderData.fulfillments = [
        {
          type: 'PICKUP',
          state: 'PROPOSED',
          pickupDetails: {
            recipient: {
              emailAddress: this.getAuthUserEmail,
              displayName: this.getAuthUserName,
            },
            autoCompleteDuration: 'P7DT0H0S',
            expiresAt: formatRFC3339(addHours(new Date(), 2)),
            scheduleType: 'SCHEDULED',
            pickupAt: formatRFC3339(addMinutes(new Date(), 30)),
            note,
          },
        },
      ]

      squareOrderData.serviceCharges = [
        {
          name: 'Minimart Service Fee',
          amountMoney: {
            amount: _get(this.order, 'service_fee', 0),
            currency: 'USD',
          },
          calculationPhase: 'TOTAL_PHASE',
          taxable: false,
        },
      ]

      return squareOrderData
    }, // squareOrderData

    deliveryFeeItem() {
      return this.getOrderItemByName(DELIVERY_FEE_ITEM_NAME)
    }, // deliveryFeeItem

    salesTaxAmount() {
      return Number(_get(this.order, 'tax', 0))
    }, // salesTaxAmount

    salesTaxPcnt() {
      if (!this.salesTaxAmount) return 0

      let subtotal = this.lineItems.reduce(
        (acc, li) => acc + li.item_price * Number(li.quantity),
        0
      )
      if (this.familyDiscountItem) {
        let familyDiscountValue = Math.abs(
          _get(this.familyDiscountItem, 'item_price', 0)
        )
        subtotal -= familyDiscountValue
      }
      const rawTaxPcnt = (this.salesTaxAmount / subtotal) * 100
      return Number(rawTaxPcnt.toFixed(5))
    }, // salesTaxPcnt

    familyDiscountItem() {
      return this.getOrderItemByName(FAMILY_DISCOUNT_ITEM_NAME)
    }, // familyDiscountItem

    totalAmountMinusTip() {
      let total = this.orderTotalInCents
      if (this.tipAmount) {
        total -= this.tipAmount
      }
      return total
    }, // totalAmountMinusTip

    tipAmount() {
      return this.order.tip
    }, // tipAmount

    buildPaymentRequest(payments) {
      return payments.paymentRequest({
        countryCode: 'US',
        currencyCode: 'USD',
        total: {
          amount: `${this.order.grand_total_usd}`,
          label: 'Total',
        },
      })
    }, // buildPaymentRequest
  },
  methods: {
    ...mapActions('order', ['updateOrderFields']),
    reset() {
      this.connectionError = ''
      this.connectionErrorDetail = null
      this.squareOrder = null
      this.squareFormLoaded = false
    }, // reset
    async initialize() {
      try {
        await this.reset()
        await this.loadSquareScripts()
      } catch (error) {
        this.connectionError = 'Could Not Connect to Square'
      }
    },
    getOrderItemByName(itemName) {
      return this.order.line_items.find((li) => li.item_name === itemName)
    },

    async tryFindOrCreate() {
      try {
        await this.reset()
        this.initSquare()
        await this.findOrCreateOrder()
        this.squareFormLoaded = true
      } catch (error) {
        this.connectionError = 'Could Not Connect'
        if (
          ['FORBIDDEN', 'UNAUTHORIZED'].includes(
            _get(error, 'response.data.0.code')
          )
        ) {
          this.connectionErrorDetail =
            'Unauthorized Action : Square has not been configured properly for this shop.'
        }
      }
    }, // tryFindOrCreate

    async findOrCreateOrder() {
      let order = await SquareService.findOrder({
        mmOrderId: this.$route.params.orderId,
        customerId: this.getAuthUserId,
      })
      if (!order) {
        try {
          order = await SquareService.createOrder(this.squareOrderData)
        } catch (error) {
          return Promise.reject(error)
        }
      }
      this.squareOrder = order
    }, // findOrCreateOrder

    async loadSquareScripts() {
      let squareScript = await document.createElement('script')
      let productionUrl = 'https://web.squarecdn.com/v1/square.js'
      let sandBoxUrl = 'https://sandbox.web.squarecdn.com/v1/square.js'

      let paymentFormSrc = SQUARE_IS_IN_SANDBOX ? sandBoxUrl : productionUrl

      squareScript.setAttribute('src', paymentFormSrc)
      document.head.appendChild(squareScript)
    }, // loadSquareScripts

    async initSquare() {
      if (!window.Square) {
        setTimeout(this.initSquare, 200)
        return
      }

      let Square = window.Square
      this.payments = await Square.payments(
        this.getSquareAppId,
        this.getSquareLocationID
      )

      await this.initCardPay()
      try {
        this.applePay = await this.initializeApplePay(this.payments)
        this.applePaySuccess = true
      } catch (error) {
        this.applePaySuccess = false
        // eslint-disable-next-line
        console.error('Initializing Apple Pay failed', error)
      }

      this.squareFormLoaded = true
    }, // initSquare

    async initCardPay() {
      this.cardPayError = false
      try {
        let card = await this.payments.card({
          style: {
            '.input-container.is-focus': {
              borderColor: '#006AFF',
            },
            '.message-text.is-error': {
              color: '#BF0020',
            },
          },
        })
        await card.attach('#card-container')
        this.card = card
        return true
      } catch (e) {
        this.cardPayError = true
        return
      }
    }, // initCardPay

    async initializeApplePay(payments) {
      const paymentRequest = await this.payments.paymentRequest({
        countryCode: 'US',
        currencyCode: 'USD',
        total: {
          amount: `${this.order.grand_total_usd}`,
          label: 'Total',
        },
      })
      const applePay = await payments.applePay(paymentRequest)
      // Note: You do not need to `attach` applePay.
      return applePay
    }, // initializeApplePay

    async handlePaymentMethodSubmission(paymentMethod) {
      try {
        // disable the submit button as we await tokenization and make a
        // payment request.
        this.paymentError = false
        this.isSubmittingPayment = true
        const token = await tokenize(paymentMethod)
        const paymentResults = await SquareService.createPayment({
          squareToken: token,
          amount: this.totalAmountMinusTip,
          orderId: this.squareOrder,
          mmOrderId: this.$route.params.orderId,
          tipAmount: this.tipAmount,
          customerId: this.getAuthUserId,
        })
        this.paymentSuccess = !!paymentResults
        this.paymentSuccessMsg = 'This Order has been successfully paid'
        if (this.paymentSuccess && this.$gtag) {
          this.$gtag.event('square_payment_success')
        }
        if (paymentResults.updatedOrder) {
          OrderModel.update({
            where: paymentResults.updatedOrder.id,
            data: paymentResults.updatedOrder,
          })
        }
      } catch (error) {
        if (
          _get(error, 'response.data.0.detail') === 'The order is already paid.'
        ) {
          this.paymentSuccessMsg =
            'This Order is already paid. If your Order is still marked "Unpaid" please contact the shop owner.'
          this.paymentSuccess = true
          return
        }

        this.paymentSuccess = false
        let errorCode = _get(error, 'response.data.code')
        let errorDetail = _get(error, 'response.data.detail')
        this.paymentError = { errorCode, errorDetail }
        if (this.$gtag) {
          this.$gtag.event('square_payment_error')
        }
      } finally {
        this.isSubmittingPayment = false
      }
    },
  }, // methods
}
</script>

<style>
.payment-border {
  color: rgba(0, 0, 0, 0.55);
  background: rgba(0, 0, 0, 0.1);
  height: 1px;
  width: 100%;
  margin: 30px 0;
  font-size: 12px;
  line-height: 20px;
  text-transform: uppercase;
  position: relative;
}
.payment-border span {
  position: absolute;
  top: -10px;
  background: #fafafa;
  padding: 0 10px;
  left: calc(50% - 1em - 5px);
}
#apple-pay-button {
  height: 48px;
  width: 100%;
  display: inline-block;
  -webkit-appearance: -apple-pay-button;
  -apple-pay-button-type: plain;
  -apple-pay-button-style: black;
}
</style>
